import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  TextField,
  LinearProgress,
  Fade,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EventIcon from '@mui/icons-material/Event';
import { useAuth } from '../../../contexts/AuthContext';
import { emitOnboardingStep } from '../../../onboarding/onboardingEvents';
import { useUserOrganizations, useOrganization } from '../hooks/useOrganization';
import { getSurvey } from '../../../services/surveyService';
import { submitResponse, saveDraftResponse, getDraftResponse, deleteDraftResponse, hasResponded, onePerPersonFor } from '../../../services/responseService';
import { SURVEY_SECTIONS } from '../utils/constants';
import RatingScale from '../../../components/Common/RatingScale';

function TakeSurvey() {
  const { id: surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data loading
  const { organizations, loading: orgsLoading } = useUserOrganizations(user?.uid);
  const [survey, setSurvey] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(true);
  const [surveyError, setSurveyError] = useState(null);

  // Use first org (MVP)
  const orgId = organizations?.[0]?.id || null;
  const { organization, loading: orgLoading } = useOrganization(orgId);

  // Step tracking: step 0 = scope selection, steps 1+ = survey sections
  const [activeStep, setActiveStep] = useState(0);

  // Scope selection state
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');

  // Survey answers: { questionKey: value }
  const [answers, setAnswers] = useState({});

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false); // one response per person (Q-22)
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    // Prefer the survey's own organization (a user in several orgs must be judged by the survey's org rules).
    const org = (organizations || []).find((o) => o.id === survey?.organizationId) || organizations?.[0] || null;
    if (!survey || !user?.uid || survey.anonymous || !onePerPersonFor(org)) { setAlreadyDone(false); return; }
    hasResponded(surveyId, user.uid).then(setAlreadyDone).catch(() => setAlreadyDone(false));
  }, [survey, user?.uid, surveyId, organizations]);
  const [submitError, setSubmitError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [draftLoaded, setDraftLoaded] = useState(false);
  const saveTimerRef = useRef(null);

  // Load survey data
  useEffect(() => {
    if (!surveyId) return;
    setSurveyLoading(true);
    getSurvey(surveyId)
      .then((s) => {
        setSurvey(s);
        setSurveyLoading(false);
      })
      .catch((err) => {
        setSurveyError(err.message);
        setSurveyLoading(false);
      });
  }, [surveyId]);

  // Load draft response on mount (resume capability)
  useEffect(() => {
    if (!surveyId || !user?.uid || draftLoaded) return;
    getDraftResponse(surveyId, user.uid)
      .then((draft) => {
        if (draft) {
          // Restore saved progress
          if (draft.answers && Object.keys(draft.answers).length > 0) {
            setAnswers(draft.answers);
          }
          if (draft.respondentScope) {
            if (draft.respondentScope.siteId) setSelectedSiteId(draft.respondentScope.siteId);
            if (draft.respondentScope.departmentId) setSelectedDepartmentId(draft.respondentScope.departmentId);
            if (draft.respondentScope.teamId) setSelectedTeamId(draft.respondentScope.teamId);
          }
          if (typeof draft.currentStep === 'number') {
            setActiveStep(draft.currentStep);
          }
        }
        setDraftLoaded(true);
      })
      .catch(() => {
        setDraftLoaded(true);
      });
  }, [surveyId, user?.uid, draftLoaded]);

  // Debounced auto-save function
  const performSave = useCallback(async (currentAnswers, currentScope, currentStep) => {
    if (!surveyId || !user?.uid) return;
    setSaveStatus('saving');
    try {
      await saveDraftResponse({
        surveyId,
        organizationId: orgId,
        respondentUid: user.uid,
        respondentScope: currentScope,
        answers: currentAnswers,
        currentStep: currentStep,
      });
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [surveyId, user?.uid, orgId]);

  // Auto-save when answers, scope, or step changes (debounced 1.5s)
  useEffect(() => {
    if (!draftLoaded || submitted) return;

    // Clear any pending save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Build scope for saving
    const currentScope = {};
    if (selectedSiteId) {
      currentScope.siteId = selectedSiteId;
      currentScope.siteName = (organization?.sites || []).find((s) => s.id === selectedSiteId)?.name || '';
    }
    if (selectedDepartmentId) {
      currentScope.departmentId = selectedDepartmentId;
      currentScope.departmentName = (organization?.departments || []).filter((d) => d.siteId === selectedSiteId).find((d) => d.id === selectedDepartmentId)?.name || '';
    }
    if (selectedTeamId) {
      currentScope.teamId = selectedTeamId;
      currentScope.teamName = (organization?.teams || []).filter((t) => t.departmentId === selectedDepartmentId).find((t) => t.id === selectedTeamId)?.name || '';
    }

    // Only save if there's something to save
    if (Object.keys(answers).length > 0 || selectedSiteId) {
      saveTimerRef.current = setTimeout(() => {
        performSave(answers, currentScope, activeStep);
      }, 1500);
    }

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [answers, selectedSiteId, selectedDepartmentId, selectedTeamId, activeStep, draftLoaded, submitted, performSave, organization]);

  // Derived org structure for cascading dropdowns
  const sites = useMemo(() => organization?.sites || [], [organization]);

  const departments = useMemo(() => {
    if (!selectedSiteId || !organization) return [];
    return (organization.departments || []).filter((d) => d.siteId === selectedSiteId);
  }, [organization, selectedSiteId]);

  const teams = useMemo(() => {
    if (!selectedDepartmentId || !organization) return [];
    return (organization.teams || []).filter((t) => t.departmentId === selectedDepartmentId);
  }, [organization, selectedDepartmentId]);

  // Reset dependent dropdowns when parent changes
  const handleSiteChange = (siteId) => {
    setSelectedSiteId(siteId);
    setSelectedDepartmentId('');
    setSelectedTeamId('');
  };

  const handleDepartmentChange = (deptId) => {
    setSelectedDepartmentId(deptId);
    setSelectedTeamId('');
  };

  // Scope selection is valid when at least a site is selected
  const isScopeValid = selectedSiteId !== '';

  // Build scope object for storage
  const buildScope = () => {
    const scope = {
      siteId: selectedSiteId,
      siteName: sites.find((s) => s.id === selectedSiteId)?.name || '',
    };
    if (selectedDepartmentId) {
      scope.departmentId = selectedDepartmentId;
      scope.departmentName = departments.find((d) => d.id === selectedDepartmentId)?.name || '';
    }
    if (selectedTeamId) {
      scope.teamId = selectedTeamId;
      scope.teamName = teams.find((t) => t.id === selectedTeamId)?.name || '';
    }
    return scope;
  };

  // Step labels for stepper
  const stepLabels = ['Select Your Scope', ...SURVEY_SECTIONS.map((s) => s.title.replace(/^Section \d+: /, ''))];

  // Total steps
  const totalSteps = stepLabels.length;

  // Handle answer change
  const handleAnswerChange = (questionKey, value) => {
    setAnswers((prev) => ({ ...prev, [questionKey]: value }));
  };

  // Current section (for steps > 0)
  const currentSection = activeStep > 0 ? SURVEY_SECTIONS[activeStep - 1] : null;

  // Check if current step is complete
  const isCurrentStepComplete = () => {
    if (activeStep === 0) return isScopeValid;
    if (!currentSection) return false;
    return currentSection.questions.every((q) => answers[q.key] !== undefined);
  };

  // Check if ALL required questions across ALL sections are answered
  const allQuestionsAnswered = useMemo(() => {
    return SURVEY_SECTIONS.every((section) =>
      section.questions.every((q) => answers[q.key] !== undefined)
    );
  }, [answers]);

  // Count total and answered questions for validation feedback
  const totalQuestions = useMemo(() =>
    SURVEY_SECTIONS.reduce((acc, s) => acc + s.questions.length, 0),
    []
  );
  const answeredQuestions = useMemo(() =>
    SURVEY_SECTIONS.reduce((acc, s) =>
      acc + s.questions.filter((q) => answers[q.key] !== undefined).length, 0),
    [answers]
  );

  // Handle submit
  const handleSubmit = async () => {
    // Final validation: ensure ALL questions are answered
    if (!allQuestionsAnswered) {
      setSubmitError(`Please answer all required questions. You have answered ${answeredQuestions} of ${totalQuestions} questions.`);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitResponse({
        surveyId,
        organizationId: orgId,
        respondentUid: survey?.anonymous ? null : user.uid,
        respondentScope: buildScope(),
        answers,
        onePerPerson: onePerPersonFor((organizations || []).find((o) => o.id === survey?.organizationId) || organizations?.[0] || null),
      });
      // Clean up draft after successful submission
      await deleteDraftResponse(surveyId, user.uid).catch(() => {});
      setSubmitted(true);
      // Onboarding: a real survey submission completes "Drift & Accumulation Perspectives".
      emitOnboardingStep('move-the-numbers');
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (surveyLoading || orgsLoading || orgLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error states
  if (surveyError) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">Failed to load survey: {surveyError}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/drew/surveys')}>
          Back to Surveys
        </Button>
      </Box>
    );
  }

  if (!survey) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="warning">Survey not found.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/drew/surveys')}>
          Back to Surveys
        </Button>
      </Box>
    );
  }

  if (survey.status !== 'active') {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="info">This survey is not currently active (status: {survey.status}).</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(`/drew/surveys/${surveyId}`)}>
          Back to Survey
        </Button>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="info">
          You need to belong to an organization to take this survey. Go to Settings → Organization to set up your organization.
        </Alert>
      </Box>
    );
  }

  // Success state
  if (alreadyDone && !submitted) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 3, justifyContent: 'center' }}>
          You have already responded to this survey. Each person can respond once.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/drew/surveys')}>Back to Surveys</Button>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 3, justifyContent: 'center' }}>
          Your response has been submitted successfully!
        </Alert>
        <Typography variant="h5" gutterBottom>
          Thank you for completing this survey.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your feedback helps measure organizational entropy and identify areas for improvement.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            onClick={() => navigate('/drew/surveys')}
          >
            Back to Surveys
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/drew')}
          >
            View Dashboard
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {survey.title}
        </Typography>
        {survey.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {survey.description}
          </Typography>
        )}
        {survey.dueDate && (
          <Chip
            icon={<EventIcon />}
            label={`Due: ${new Date(survey.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
            size="small"
            color={(() => {
              const due = new Date(survey.dueDate);
              due.setHours(23, 59, 59, 999);
              return due < new Date() ? 'warning' : 'default';
            })()}
            variant="outlined"
          />
        )}
      </Box>

      {/* Anonymous survey indicator */}
      {survey.anonymous && (
        <Alert
          severity="info"
          icon={<VisibilityOffIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            This is an anonymous survey.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Your identity will not be stored with your responses. Only your organizational scope (site, department, NWT) is collected for aggregation purposes.
          </Typography>
        </Alert>
      )}

      {/* Stepper + Save Indicator */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        {saveStatus === 'saving' && (
          <Chip
            icon={<CloudUploadIcon />}
            label="Saving..."
            size="small"
            color="default"
            variant="outlined"
          />
        )}
        {saveStatus === 'saved' && (
          <Chip
            icon={<CloudDoneIcon />}
            label="Progress saved"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {saveStatus === 'error' && (
          <Chip
            label="Save failed"
            size="small"
            color="error"
            variant="outlined"
          />
        )}
      </Box>
      {/* Phones: icons only plus the current step's name underneath — five long labels in 390 px
          became one-word columns (UI pass 2026-09-21). Tablets and up keep the labelled stepper. */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: { xs: 0.5, sm: 2 } }}>
        {stepLabels.map((label, index) => (
          <Step key={label} completed={activeStep > index}>
            <StepLabel sx={{ '& .MuiStepLabel-label': { display: { xs: 'none', sm: 'block' } } }}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, textAlign: 'center', mb: 2, fontWeight: 600 }}>
        Step {activeStep + 1} of {stepLabels.length} · {stepLabels[activeStep]}
      </Typography>

      {/* Linear progress bar showing section progress (1-5) */}
      {activeStep > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Section {activeStep} of {SURVEY_SECTIONS.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round((activeStep / SURVEY_SECTIONS.length) * 100)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(activeStep / SURVEY_SECTIONS.length) * 100}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {/* Step Content with smooth transition */}
      <Fade in={true} key={activeStep} timeout={400}>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {activeStep === 0 ? (
            /* ===== SCOPE SELECTION STEP ===== */
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOnIcon color="primary" />
                <Typography variant="h6">
                  Select Your Organizational Scope
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please select where you work within the organization. This helps aggregate
                responses at each level for more meaningful insights.
              </Typography>

              <Stack spacing={3}>
                {/* Site dropdown */}
                <FormControl fullWidth required>
                  <InputLabel id="scope-site-label">Site</InputLabel>
                  <Select
                    labelId="scope-site-label"
                    id="scope-site-select"
                    value={selectedSiteId}
                    label="Site"
                    onChange={(e) => handleSiteChange(e.target.value)}
                  >
                    {sites.length === 0 ? (
                      <MenuItem value="" disabled>
                        No sites configured
                      </MenuItem>
                    ) : (
                      sites.map((site) => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.name}{site.location ? ` (${site.location})` : ''}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Department dropdown - filtered by selected site */}
                <FormControl fullWidth disabled={!selectedSiteId || departments.length === 0}>
                  <InputLabel id="scope-dept-label">Department</InputLabel>
                  <Select
                    labelId="scope-dept-label"
                    id="scope-dept-select"
                    value={selectedDepartmentId}
                    label="Department"
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                  >
                    {departments.length === 0 ? (
                      <MenuItem value="" disabled>
                        {selectedSiteId ? 'No departments for this site' : 'Select a site first'}
                      </MenuItem>
                    ) : (
                      departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Team dropdown - filtered by selected department */}
                <FormControl fullWidth disabled={!selectedDepartmentId || teams.length === 0}>
                  <InputLabel id="scope-team-label">Team</InputLabel>
                  <Select
                    labelId="scope-team-label"
                    id="scope-team-select"
                    value={selectedTeamId}
                    label="Natural Work Team (NWT)"
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                  >
                    {teams.length === 0 ? (
                      <MenuItem value="" disabled>
                        {selectedDepartmentId ? 'No teams for this department' : 'Select a department first'}
                      </MenuItem>
                    ) : (
                      teams.map((team) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Stack>

              {sites.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No sites have been configured for this organization. Please contact your organization admin
                  to set up the org structure in Settings → Organization.
                </Alert>
              )}
            </Box>
          ) : (
            /* ===== SURVEY QUESTIONS STEP ===== */
            <Box>
              <Typography variant="h6" gutterBottom>
                {currentSection.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {currentSection.instructions}
              </Typography>

              <Stack spacing={4}>
                {currentSection.questions.map((question, idx) => {
                  const value = answers[question.key];
                  return (
                    <Paper
                      key={question.key}
                      variant="outlined"
                      sx={{ p: 2.5 }}
                    >
                      <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                        {idx + 1}. {question.text}
                      </Typography>
                      <RatingScale
                        value={value}
                        onChange={(newVal) => handleAnswerChange(question.key, newVal)}
                        minLabel={currentSection.scaleLabels?.min}
                        maxLabel={currentSection.scaleLabels?.max}
                      />
                    </Paper>
                  );
                })}

                {/* Free text field for qualitative notes */}
                {currentSection.notesKey && (
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                      Additional Comments
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={6}
                      placeholder={currentSection.notesLabel || 'Share any additional observations or examples...'}
                      value={answers[currentSection.notesKey] || ''}
                      onChange={(e) => handleAnswerChange(currentSection.notesKey, e.target.value)}
                      variant="outlined"
                      size="small"
                      helperText="Optional — your qualitative feedback helps provide context for the ratings above."
                    />
                  </Paper>
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
      </Fade>

      {/* Submit error */}
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to submit response: {submitError}
        </Alert>
      )}

      {/* Navigation buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            if (activeStep === 0) {
              navigate(`/drew/surveys/${surveyId}`);
            } else {
              setActiveStep((prev) => prev - 1);
            }
          }}
        >
          {activeStep === 0 ? 'Back to Survey' : 'Previous'}
        </Button>

        {activeStep < totalSteps - 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => setActiveStep((prev) => prev + 1)}
            disabled={!isCurrentStepComplete()}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            endIcon={<SendIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={submitting || !isCurrentStepComplete() || !allQuestionsAnswered}
          >
            {submitting ? 'Submitting...' : 'Submit Response'}
          </Button>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Submit Your Response?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to submit your survey response. Once submitted, you will not be able to change your answers.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Site:</strong> {sites.find((s) => s.id === selectedSiteId)?.name || '—'}
            </Typography>
            {selectedDepartmentId && (
              <Typography variant="body2" color="text.secondary">
                <strong>Department:</strong> {departments.find((d) => d.id === selectedDepartmentId)?.name || '—'}
              </Typography>
            )}
            {selectedTeamId && (
              <Typography variant="body2" color="text.secondary">
                <strong>Team:</strong> {teams.find((t) => t.id === selectedTeamId)?.name || '—'}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Questions answered:</strong> {Object.keys(answers).length} / {SURVEY_SECTIONS.reduce((acc, s) => acc + s.questions.length, 0)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setConfirmOpen(false);
              handleSubmit();
            }}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Confirm & Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TakeSurvey;
